/**
 * PTBFFixationListPage — danh sách PTBF Fixations. Đồng bộ ContractListView.
 * Toolbar enable/disable theo Access frmPurchaseMainSubFormPTBFFixation:
 *   Form_Load gate SCREEN_ACCESS (=canEdit) cho New/Edit/Delete/Print;
 *   Edit/Delete/Print chỉ cần 1 record đã chọn (btn*_Click check ptbf_p_id).
 */
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Snackbar, Stack, Table, TableBody,
  TableCell, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintIcon from '@mui/icons-material/Print'
import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import RoleGuard from '@/components/RoleGuard'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TABLE_PAGINATION_COMPACT, TEXT_TRUNCATE } from '@/theme/responsive'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeletePTBFFixationMutation, useLazyPrintPTBFFixationQuery, useListPTBFFixationsQuery,
} from '@/store/api/purchaseContractApi'
import type { PTBFFixationOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { titleCaseName } from '@/utils/text'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml } from '@/utils/printPreview'
import { sumNumericColumn } from '@/utils/tableTotals'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import PTBFFixationFormDialog from './PTBFFixationFormDialog'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

type FilterKind = 'search'
const ALL_ROWS_PAGE_SIZE = 1_000_000

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

// Inject `@page { size: A4; margin: 0 !important }` để suppress browser-injected
// page header (URL/date) + footer (page number). `!important` override base
// template `_print_base.html` (margin 9mm 11mm). Mobile screen: `.report-page`
// min-height auto để bỏ vùng trống cuối iframe preview (BE reserves 276mm cho A4).
function buildPTBFPrintPreviewHtml(rawHtml: string): string {
  return buildPrintPreviewHtml(rawHtml, { styleId: 'purchase-print-preview-overrides', layout: 'flow' })
}

type PTBFPrintTarget = { ptbfPId: number; contractNo: string }

interface PTBFFixationPrintDialogProps {
  open: boolean
  printTarget: PTBFPrintTarget | null
  canEdit?: boolean
  onClose: () => void
}

/**
 * Print Preview - PTBF Fixation.
 * Access parity: `btnPrintout_Click` → `DoCmd.OpenReport "rpPurchaseFixation"`.
 * Auto-fetches HTML on open, renders in iframe srcDoc (LocalP "Print Preview - Cancel" pattern);
 * Print button uploads HTML and prints via `printAndUploadReport`.
 */
function PTBFFixationPrintDialog({ open, printTarget, canEdit = true, onClose }: PTBFFixationPrintDialogProps) {
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewKey, setPreviewKey] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [triggerPrint, { isFetching }] = useLazyPrintPTBFFixationQuery()
  const autoPreviewKeyRef = useRef<number | null>(null)

  const ptbfPId = printTarget?.ptbfPId ?? 0
  const contractNo = printTarget?.contractNo ?? ''

  const handleClosePreview = useCallback(() => {
    setPreviewHtml('')
    setPreviewKey('')
  }, [])

  useEffect(() => {
    if (!open) {
      autoPreviewKeyRef.current = null
      return
    }
    if (!ptbfPId || autoPreviewKeyRef.current === ptbfPId) return
    autoPreviewKeyRef.current = ptbfPId
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await triggerPrint({ id: ptbfPId, kind: 'main' }).unwrap()
          if (!result.data.html) {
            setErrorMessage('Print preview could not open because the server did not return a document.')
            onClose()
            return
          }
          setPreviewHtml(buildPTBFPrintPreviewHtml(result.data.html))
          setPreviewKey(contractNo || String(ptbfPId))
          setToastMessage(result.message ?? 'Print preview generated.')
          onClose()
        } catch (err) {
          setErrorMessage(getErrorMessage(err, 'Print PTBF Fixation failed'))
          onClose()
        }
      })()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [open, ptbfPId, contractNo, triggerPrint, onClose])

  return (
    <>
      <Dialog open={open && isFetching && !previewHtml} maxWidth="xs" fullWidth>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 4 }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Generating print preview…</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(previewHtml)} onClose={handleClosePreview} maxWidth={false} fullWidth PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title="Print Preview - PTBF Fixation"
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>PTBF Fixation</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>PTBF</Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClosePreview} size="small">Close</Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!previewHtml}
                onClick={() => {
                  const printTitle = `${sanitizePdfFileNamePart(previewKey || 'ptbf-fixation')}_PTBF_Fixation.pdf`
                  void printAndUploadReport({
                    iframeTitle: 'PTBF Fixation Preview',
                    reportName: 'rpPurchaseFixation',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title="PTBF Fixation Preview"
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(errorMessage)} onClose={() => setErrorMessage('')} maxWidth="xs" fullWidth>
        <DialogTitle>Print Error</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" size="small" onClick={() => setErrorMessage('')}>OK</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2000}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}

const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'number' | 'bool'
type ColumnDef = { key: Extract<keyof PTBFFixationOut, string>; label: string; kind?: ColumnKind; align?: 'right'; digits?: number; filter?: ColumnFilterKind; sticky?: boolean; width?: number; plainInt?: boolean }
const NON_SUM_TOTAL_KEYS = new Set<string>(['ptbf_p_id', 'market_level', 'ptbf_diffs', 'final_price', 'lot_to_be_fixed'])

// 16 cột — map 1:1 thứ tự datasheet Access `frmPurchaseQryPTBFFixation`.
// Access ControlSource `supplier_name` → BE rename `name` (service map r.supplier_name → r.name).
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'ptbf_p_id', label: 'ID', kind: 'number', align: 'right', digits: 0, plainInt: true },
  { key: 'upload_txt', label: 'IsUploaded?', filter: 'checkboxes' },
  { key: 'fixation_date', label: 'FixationDate', kind: 'date' },
  { key: 'contract_no', label: 'Contract No', filter: 'checkboxes', sticky: true, width: 130 },
  { key: 'name', label: 'CP Name', filter: 'checkboxes' },
  { key: 'quality_code', label: 'Quality Code', filter: 'checkboxes' },
  { key: 'total_lot', label: 'Total lot', kind: 'number', align: 'right', digits: 0 },
  { key: 'lot_to_be_fixed', label: 'Lot to be fixed', kind: 'number', align: 'right', digits: 0 },
  { key: 'lot_fixed', label: 'Lot fixed', kind: 'number', align: 'right', digits: 0 },
  { key: 'market_level', label: 'Market level', kind: 'number', align: 'right', digits: 2 },
  { key: 'cross_check_fix_level', label: 'Cross check fixed level' },
  { key: 'futures_month', label: 'Futures month', filter: 'checkboxes' },
  { key: 'ptbf_diffs', label: 'Diffs', kind: 'number', align: 'right', digits: 2 },
  { key: 'final_price', label: 'Final price', kind: 'number', align: 'right', digits: 2 },
  { key: 'issued_date', label: 'IssuedDate', kind: 'date' },
  { key: 'note', label: 'Note' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatNumberFixed = (v: number, digits = 0): string => digits > 0 ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : (Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const cellValue = (row: PTBFFixationOut, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  // `quality_code` ở list view BE trả string (concat), ở table base là array — handle cả 2.
  if (Array.isArray(val)) return val.join(', ')
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'bool') return val ? 'Yes' : 'No'
  if (col.plainInt) { const n = typeof val === 'number' ? val : Number(val); return Number.isFinite(n) ? String(Math.trunc(n)) : String(val) }
  if (col.kind === 'number' && typeof val === 'number') return formatNumberFixed(val, col.digits ?? 0)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return formatNumberFixed(val, 0)
  if (col.key === 'name') return titleCaseName(val)
  return String(val)
}

export function PTBFFixationListPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const { selectedId, toggleSelect } = useRowSelection<number>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const { getWidth, startResize } = useColumnWidths('purchase.ptbf-fixation')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<ColumnDef['key']> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: ColumnDef['key']
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => { setSearchTerm(''); columnFilters.clearAll(); setPage(0); closeFilterPopover() }

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<PTBFFixationOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<PTBFFixationOut | null>(null)
  const [printTarget, setPrintTarget] = useState<PTBFPrintTarget | null>(null)

  const { data, isLoading, isFetching, error } = useListPTBFFixationsQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE }, { refetchOnMountOrArgChange: true })
  const [deleteFixation, { isLoading: deleting }] = useDeletePTBFFixationMutation()

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: 'ptbf-fixation',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'purchase.ptbf-fixation',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const minTableWidth = useMemo(
    () => Math.max(1200, visibleColumns.length * 150),
    [visibleColumns.length],
  )

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row as unknown as Record<string, unknown>, searchTerm, TABLE_COLUMNS as unknown as { key: string }[])) return false
      if (!rowMatchesAllColumnFilters(row as unknown as Record<string, unknown>, columnFilters.filters)) return false
      return true
    }), [baseRows, searchTerm, columnFilters.filters])

  const sortedRows = useMemo(
    () =>
      sortTableRows(
        filteredRows,
        sortState,
        visibleColumns,
        (row, key) => {
          const v = row[key as keyof PTBFFixationOut]
          return Array.isArray(v) ? v.join(', ') : v
        },
      ),
    [filteredRows, sortState, visibleColumns],
  )
  const rows = useMemo(() => rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [page, rowsPerPage, sortedRows])

  // Per-column ▽ popover metadata
  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows as never) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows as unknown as Record<string, unknown>[], String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns.filter(
          (col) => col.kind === 'number' && !col.plainInt && typeof col.key === 'string' && !col.key.startsWith('_') && !NON_SUM_TOTAL_KEYS.has(String(col.key)),
        ).map((col) => [String(col.key), sumNumericColumn(filteredRows, col.key as keyof PTBFFixationOut)]),
      ),
    [filteredRows, visibleColumns],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.ptbf_p_id === selectedId) ?? null, [baseRows, selectedId])
  const toggleSort = (key: ColumnDef['key']) => { setSortState((current) => toggleTableSort(current, key)); setPage(0) }

  const handleEdit = () => { if (!selectedRow) return; setEditing(selectedRow) }
  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try { await deleteFixation(confirmDel.ptbf_p_id).unwrap(); snack.success('Fixation deleted'); setConfirmDel(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Failed to delete fixation')) }
  }
  const handlePrint = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setPrintTarget({ ptbfPId: selectedRow.ptbf_p_id, contractNo: selectedRow.contract_no ?? '' })
  }

  // Access parity: Form_Load gates New/Edit/Delete/Print on SCREEN_ACCESS (=canEdit);
  // btnEdit_Click / btnDelete_Click only require a selected record (ptbf_p_id exists).
  // No is_upload disable — uploaded fixations open for view; Save is locked inside the
  // dialog via `lockedByUpload` (mirrors Access Add-form is_upload=-1 lock).
  const rowActionDisabled = !selectedRow || !canEdit

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Bảng FILL hết không gian flex còn lại thay vì clamp height theo viewport
        // → bỏ khoảng trống dưới bảng. Override clamp của `.app-dataframe`.
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box></Button>
            <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={rowActionDisabled} onClick={handleEdit} startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} title="Edit"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box></Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={rowActionDisabled} onClick={() => selectedRow && setConfirmDel(selectedRow)} startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} title="Delete"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box></Button>
            <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={rowActionDisabled} onClick={handlePrint} startIcon={<PrintIcon sx={{ fontSize: 16 }} />} title="Pr-Fixation"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Pr-Fixation</Box></Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button size="small" variant="outlined" endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openMenu(e.currentTarget)} sx={filterBarBtnSx} title="Columns">
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />


      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={TABLE_PAGINATION_COMPACT} />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract no, supplier..."
              label="Search PTBF Fixations"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => { if (!colPopover) return; columnFilters.setFilter(String(colPopover.key), v); setPage(0) }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load PTBF Fixations"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No PTBF Fixations found"
              description="Try adjusting the search, futures month, or uploaded filter above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const w = getWidth(String(col.key), col.width)
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align}
                        className={`app-table-header-cell${col.sticky ? ' app-sticky-col' : ''}`}
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        // userSelect:'text' để bôi đen + copy tên cột (click-sort vẫn chạy).
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', position: 'relative' }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(String(col.key))}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const total = totalRowValues.get(String(col.key))
                    const val = total == null ? '' : formatNumberFixed(total, col.digits ?? 0)
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align}
                        className={col.sticky ? 'app-sticky-col' : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: PTBFFixationOut) =>
                visibleColumns.map((col) => {
                  const val = cellValue(row, col)
                  const w = getWidth(String(col.key), col.width)
                  const wrap = getWidth(String(col.key)) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align}
                        className={col.sticky ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={col.sticky ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const id = Number.isFinite(idx) ? rows[idx]?.ptbf_p_id : null
                  return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) toggleSelect(id) }} />
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <PTBFFixationFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
      <PTBFFixationFormDialog open={!!editing} mode="edit" initialValue={editing} onClose={() => setEditing(null)} />
      <PTBFFixationPrintDialog
        open={Boolean(printTarget)}
        printTarget={printTarget}
        canEdit={canEdit}
        onClose={() => setPrintTarget(null)}
      />
      <ConfirmDialog open={!!confirmDel} title="Delete Fixation" message={confirmDel ? `Delete fixation #${confirmDel.ptbf_p_id} of contract ${confirmDel.contract_no}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDelete()} onCancel={() => setConfirmDel(null)} />
    </Box>
  )
}

export default PTBFFixationListPage
